import {type ReactNode, type Ref} from 'react';
import {LayoutGrid, List, Network, Rows3, Tag, X} from 'lucide-react';
import {
  Button,
  Separator,
  ToggleGroup,
  ToggleGroupItem,
  CopyButton
} from '@kroll-resolver-crisp/ui-components-react';
import {type ItemSort} from '@/api/items';
import {BulkFlagMenu} from '@/components/flags/Flag';
import {FlagFilterChips, FlagFilterMenu} from '@/components/flags/FlagFilterMenu';
import {SortMenu} from '@/components/items/SortMenu';
import {type FlagFilter} from '@/lib/flagFilter';
import {type FlagColor} from '@/api/flags';
import {FilterBar} from '@/components/items/FilterBar';
import {ResponsiveThumbnailSizeControl} from '@/components/shared/ThumbnailSizeControl';
import {
  type ItemsView,
  THUMBNAIL_SIZE_MAX,
  THUMBNAIL_SIZE_MIN,
  type ThumbnailSize,
  useUiStore
} from '@/store/uiStore';

// The grid/list/detailed-list switch. Persisted, and shared by every page that shows items. A page with another
// way to show the same results (the follower network) passes it as `network`: it is a third
// segment of the same switch, open only until grid or list is picked again, and not persisted.
export function ItemsViewToggle({
  network
}: {
  readonly network?: {readonly open: boolean; readonly onOpenChange: (open: boolean) => void};
} = {}) {
  const view = useUiStore((s) => s.itemsView);
  const setView = useUiStore((s) => s.setItemsView);

  return (
    <ToggleGroup
      variant="outline"
      spacing={0}
      size="sm"
      aria-label="Layout"
      value={[network?.open ? 'network' : view]}
      onValueChange={(next) => {
        const picked = next[0] as ItemsView | 'network' | undefined;
        if (picked === 'network') {
          network?.onOpenChange(true);
        } else if (picked === 'grid' || picked === 'list' || picked === 'detailed') {
          setView(picked);
          network?.onOpenChange(false);
        }
      }}>
      <ToggleGroupItem value="grid" aria-label="Grid view">
        <LayoutGrid />
      </ToggleGroupItem>
      <ToggleGroupItem value="list" aria-label="List view">
        <List />
      </ToggleGroupItem>
      <ToggleGroupItem
        value="detailed"
        aria-label="Detailed list view"
        title="Detailed list: descriptions and tags shown for every item">
        <Rows3 />
      </ToggleGroupItem>
      {network ? (
        <ToggleGroupItem value="network" aria-label="Follower network" title="Follower network">
          <Network />
        </ToggleGroupItem>
      ) : null}
    </ToggleGroup>
  );
}

// Left to right: [leading: the page's tabs], search, [children: the page's own filters, e.g. match
// strength], flags, the Filters control, result count, a spacer, sort, view toggle. Active flag filters sit on a second
// row as removable chips.
export function ItemsToolbar({
  search,
  onSearchChange,
  searchPlaceholder = 'Search items…',
  flags,
  onFlagsChange,
  filters,
  resultCount,
  sort,
  onSortChange,
  flaggedSort,
  leading,
  trailing,
  children
}: {
  // Omit `search` and the box isn't shown (a page with no free-text filter of its own).
  readonly search?: string;
  readonly onSearchChange?: (value: string) => void;
  readonly searchPlaceholder?: string;
  readonly flags: readonly FlagFilter[];
  readonly onFlagsChange: (next: FlagFilter[]) => void;
  // The page's Filters control (with its own count badge) - the panel's contents are the page's.
  readonly filters?: ReactNode;
  readonly resultCount?: number | undefined;
  readonly sort?: ItemSort;
  readonly onSortChange?: (next: ItemSort) => void;
  // Offers the "Date flagged" sort - while a flag filter narrows the page to flagged items.
  readonly flaggedSort?: boolean;
  // Sits before the search box - the Items | Channels tabs, as in the investigations toolbar.
  readonly leading?: ReactNode;
  // Page-specific view controls, ahead of the sort - the blur toggle, as in the investigations toolbar.
  readonly trailing?: ReactNode;
  readonly children?: ReactNode;
}) {
  const view = useUiStore((s) => s.itemsView);
  const thumbnailSize = useUiStore((s) => s.thumbnailSize);
  const setThumbnailSize = useUiStore((s) => s.setThumbnailSize);

  return (
    <div className="flex flex-col gap-2">
      <FilterBar
        leading={leading}
        search={
          search !== undefined && onSearchChange
            ? {value: search, onChange: onSearchChange, placeholder: searchPlaceholder}
            : undefined
        }>
        {children}
        <FlagFilterMenu value={flags} onChange={onFlagsChange} />
        {filters}
        {resultCount === undefined ? null : (
          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
            {resultCount.toLocaleString()} {resultCount === 1 ? 'item' : 'items'}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {trailing}
          {sort && onSortChange ? (
            <SortMenu value={sort} flagged={flaggedSort ?? false} onChange={onSortChange} />
          ) : null}
          {view === 'grid' ? (
            <ResponsiveThumbnailSizeControl
              min={THUMBNAIL_SIZE_MIN}
              max={THUMBNAIL_SIZE_MAX}
              value={thumbnailSize}
              onChange={(level) => {
                setThumbnailSize(level as ThumbnailSize);
              }}
            />
          ) : null}
          <ItemsViewToggle />
        </div>
      </FilterBar>
      <FlagFilterChips value={flags} onChange={onFlagsChange} />
    </div>
  );
}

// Shown while anything is selected. `children` carries a page's own extra actions (dismiss,
// translate) between Flag and Copy IDs.
export function ItemsBulkBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onFlag,
  showClearFlag,
  copyIds,
  copyIdsRef,
  onClassify,
  onClear,
  children
}: {
  readonly selectedCount: number;
  // Rows the analyst could select in total; "Select all N" shows while fewer are selected.
  readonly totalCount: number;
  readonly onSelectAll?: (() => void) | undefined;
  readonly onFlag?: ((color: FlagColor | null) => void) | undefined;
  readonly showClearFlag?: boolean;
  // The ids to copy, worked out at click time.
  readonly copyIds: () => string;
  // Lets a keyboard shortcut press the Copy IDs button, so it shows the same confirmation.
  readonly copyIdsRef?: Ref<HTMLButtonElement> | undefined;
  readonly onClassify?: (() => void) | undefined;
  readonly onClear: () => void;
  readonly children?: ReactNode;
}) {
  if (selectedCount === 0) {
    return null;
  }

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      className="border-border bg-card flex shrink-0 flex-wrap items-center gap-2 border-t px-5 py-2">
      <span className="text-xs">
        <strong className="font-semibold">{selectedCount}</strong> selected
      </span>

      <Separator decorative orientation="vertical" className="h-4" />

      {onSelectAll && selectedCount < totalCount ? (
        <Button variant="outline" size="sm" onClick={onSelectAll}>
          Select all {totalCount.toLocaleString()}
        </Button>
      ) : null}

      {onFlag ? <BulkFlagMenu showClear={showClearFlag ?? false} onSelect={onFlag} /> : null}

      {children}

      <CopyButton ref={copyIdsRef} variant="outline" size="sm" label="Copy IDs" value={copyIds} />

      <div className="ml-auto flex items-center gap-2">
        {onClassify ? (
          <Button size="sm" onClick={onClassify}>
            <Tag />
            Classify {selectedCount}
          </Button>
        ) : null}
        <Button variant="ghost" size="icon-sm" aria-label="Clear selection" onClick={onClear}>
          <X />
        </Button>
      </div>
    </div>
  );
}
